// src/pages/Loans.jsx
import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  Button,
  Paper,
  Fade,
  Grow,
  Zoom,
  alpha
} from '@mui/material';
import {
  Home,
  DirectionsCar,
  Agriculture,
  School,
  Business,
  Receipt,
  Calculate,
  TrendingUp,
  ArrowForward,
  Phone,
  Email,
  LocationOn,
  CheckCircle,
  Diamond,
  Security,
  Schedule
} from '@mui/icons-material';

// Import images from public folder
const loansHero = '/images/herosection.png';
const homeLoanImage = '/images/DB - Retail Shopfront-mockup (June 24, 2018).png';
const agriLoanImage = '/images/db2 top (2).png';
const vehicleLoanImage = '/images/db2 top (3).png';
const educationLoanImage = '/images/db2 top.png';

// DiamondBank Zimbabwe color palette
const colors = {
  primary: '#470a91',
  primaryLight: '#5e1bb5',
  primaryDark: '#35076d',
  secondary: '#d2d8e1',
  secondaryDark: '#b0b8c4',
  accent: '#000000',
  lightBg: '#f8f9fa',
  textDark: '#1a1a1a',
  textLight: '#ffffff',
  gray: '#6b7280'
};

const Loans = () => {
  const loanProducts = [
    {
      icon: <Home sx={{ fontSize: 40, color: colors.primary }} />,
      title: "Home Loans",
      description: "Finance your dream home in Zimbabwe with competitive rates and flexible repayment options tailored to your needs.",
      features: [
        "Up to 25-year repayment period",
        "Competitive interest rates from 9.5%",
        "Up to 100% financing available",
        "Pre-approval within 24 hours"
      ],
      action: "Apply Now",
      image: homeLoanImage,
      gradient: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`
    },
    {
      icon: <DirectionsCar sx={{ fontSize: 40, color: colors.primary }} />,
      title: "Vehicle Loans",
      description: "Get behind the wheel of your new or used vehicle in Zimbabwe with our affordable auto financing solutions.",
      features: [
        "Up to 72-month repayment terms",
        "Quick approval within 48 hours",
        "Insurance options available",
        "Low down payments from 10%"
      ],
      action: "Calculate Payment",
      image: vehicleLoanImage,
      gradient: `linear-gradient(135deg, ${colors.primaryLight} 0%, ${colors.primary} 100%)`
    },
    {
      icon: <Agriculture sx={{ fontSize: 40, color: colors.primary }} />,
      title: "Agricultural Loans",
      description: "Specialized financing solutions designed specifically for Zimbabwean farmers and agribusinesses.",
      features: [
        "Seasonal repayment options",
        "Equipment and machinery financing",
        "Crop and livestock insurance",
        "Working capital facilities"
      ],
      action: "Learn More",
      image: agriLoanImage,
      gradient: `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.secondaryDark} 100%)`
    },
    {
      icon: <School sx={{ fontSize: 40, color: colors.primary }} />,
      title: "Education Loans",
      description: "Invest in your future or your children's education with our flexible education financing in Zimbabwe.",
      features: [
        "Local and international studies",
        "Deferred payment options",
        "Competitive student interest rates",
        "Parent and guardian loan options"
      ],
      action: "Apply Now",
      image: educationLoanImage,
      gradient: `linear-gradient(135deg, ${colors.primaryDark} 0%, ${colors.primary} 100%)`
    }
  ];

  const loanFeatures = [
    {
      icon: <Business sx={{ fontSize: 40, color: colors.primary }} />,
      title: "Business Growth",
      description: "Funding solutions to help your Zimbabwean business expand and thrive"
    },
    {
      icon: <Receipt sx={{ fontSize: 40, color: colors.primary }} />,
      title: "Flexible Terms",
      description: "Customizable repayment plans to suit your cash flow needs"
    },
    {
      icon: <Calculate sx={{ fontSize: 40, color: colors.primary }} />,
      title: "Easy Calculators",
      description: "Online tools to help you plan your loan repayments in ZWL"
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40, color: colors.primary }} />,
      title: "Competitive Rates",
      description: "Affordable interest rates starting from 8.5% APR"
    }
  ];

  const calculatorTools = [
    {
      title: "Home Loan Calculator",
      description: "Calculate your monthly home loan repayments in ZWL",
      link: "/calculators/home-loan"
    },
    {
      title: "Vehicle Loan Calculator",
      description: "Estimate your car loan payments in ZWL",
      link: "/calculators/vehicle-loan"
    },
    {
      title: "Personal Loan Calculator",
      description: "Plan your personal loan repayments in ZWL",
      link: "/calculators/personal-loan"
    },
    {
      title: "Loan Comparison Tool",
      description: "Compare different loan options in ZWL",
      link: "/calculators/loan-comparison"
    }
  ];

  return (
    <Box sx={{ overflowX: 'hidden', backgroundColor: colors.lightBg }}>
      {/* Hero Section - Updated for DiamondBank Zimbabwe */}
      <Box
        sx={{
          height: '70vh',
          minHeight: '500px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: `linear-gradient(135deg, ${colors.primary}dd, ${colors.primaryDark}aa), url(${loansHero})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: 'white',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: '100px',
            background: `linear-gradient(to top, ${colors.lightBg}, transparent)`,
            zIndex: 1
          }
        }}
      >
        {/* Animated background pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: `radial-gradient(circle at 20% 50%, ${colors.secondary} 2%, transparent 2.5%)`,
          backgroundSize: '30px 30px',
          animation: 'pulse 20s linear infinite'
        }} />

        <Container>
          <Fade in timeout={800}>
            <Box>
              <Diamond sx={{ fontSize: 60, color: colors.secondary, mb: 2, animation: 'spin 10s linear infinite' }} />
              <Typography
                variant="h1"
                gutterBottom
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2.5rem', md: '4rem' },
                  textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                  mb: 3,
                  lineHeight: 1.2
                }}
              >
                <Box component="span" sx={{ color: colors.secondary }}>Loan</Box> Solutions
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  maxWidth: '800px',
                  mx: 'auto',
                  mb: 4,
                  textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  fontSize: { xs: '1.1rem', md: '1.5rem' },
                  fontWeight: 300,
                  opacity: 0.95
                }}
              >
                Competitive rates and flexible terms to help you achieve your financial goals in Zimbabwe
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  href="/apply"
                  sx={{
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                    borderRadius: '50px',
                    background: `linear-gradient(135deg, ${colors.secondary}, ${colors.white})`,
                    color: colors.primary,
                    boxShadow: `0 4px 20px ${colors.primary}80`,
                    '&:hover': {
                      boxShadow: `0 6px 25px ${colors.primary}b3`,
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s ease',
                    fontWeight: 600
                  }}
                >
                  Apply Now
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  href="/rates"
                  sx={{
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                    borderRadius: '50px',
                    borderWidth: '2px',
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      borderWidth: '2px',
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s ease',
                    fontWeight: 600
                  }}
                >
                  View Rates
                </Button>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>

      <Container sx={{ py: 10 }}>
        {/* Loan Products Section */}
        <Box sx={{ mb: 10 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="subtitle1"
              sx={{
                color: colors.primary,
                fontWeight: 600,
                letterSpacing: 1,
                mb: 1
              }}
            >
              FINANCING OPTIONS - ZIMBABWE
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                color: colors.primary,
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -15,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '100px',
                  height: '4px',
                  background: `linear-gradient(90deg, ${colors.primary}, ${colors.secondary})`,
                  borderRadius: '2px'
                }
              }}
            >
              Our Loan Products
            </Typography>
            <Typography variant="h5" sx={{ mt: 3, color: colors.gray, fontWeight: 300 }}>
              Tailored financing solutions for every need in Zimbabwe
            </Typography>
          </Box>
          <Grid container spacing={4} justifyContent="center">
            {loanProducts.map((loan, index) => (
              <Grid item xs={12} sm={6} lg={5} key={index}>
                <Zoom in timeout={800} style={{ transitionDelay: `${index * 100}ms` }}>
                  <Card
                    elevation={3}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: { xs: 'column', md: 'row' },
                      borderRadius: '16px',
                      overflow: 'hidden',
                      background: 'white',
                      boxShadow: `0 10px 30px ${alpha(colors.primary, 0.08)}`,
                      border: `1px solid ${alpha(colors.primary, 0.1)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: `0 15px 35px ${alpha(colors.primary, 0.15)}`,
                        borderColor: `${alpha(colors.primary, 0.2)}`,
                        transform: 'translateY(-10px)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: '100%', md: 200 },
                        minHeight: { xs: 200, md: 'auto' },
                        background: loan.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          width: '100%',
                          height: '20px',
                          background: 'linear-gradient(to bottom, transparent, white)'
                        }
                      }}
                    >
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          backgroundColor: 'rgba(255,255,255,0.3)',
                          backdropFilter: 'blur(5px)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                        }}
                      >
                        {loan.icon}
                      </Box>
                    </Box>
                    <Box sx={{ p: 4, flex: 1 }}>
                      <Typography variant="h4" gutterBottom sx={{ color: colors.primary, fontWeight: 600 }}>
                        {loan.title}
                      </Typography>
                      <Typography variant="body1" paragraph sx={{ color: colors.gray, mb: 2 }}>
                        {loan.description}
                      </Typography>
                      <Box sx={{ mb: 3 }}>
                        {loan.features.map((feature, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <CheckCircle sx={{ color: colors.primary, fontSize: 18, mr: 2 }} />
                            <Typography variant="body2" sx={{ color: colors.textDark }}>{feature}</Typography>
                          </Box>
                        ))}
                      </Box>
                      <Button
                        variant="contained"
                        fullWidth
                        href={`/loans/${loan.title.toLowerCase().replace(/\s+/g, '-')}`}
                        sx={{
                          borderRadius: '50px',
                          py: 1.5,
                          background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
                          fontWeight: 600,
                          '&:hover': {
                            background: `linear-gradient(135deg, ${colors.primaryLight}, ${colors.primary})`,
                            transform: 'translateY(-2px)'
                          }
                        }}
                      >
                        {loan.action}
                      </Button>
                    </Box>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Features Section */}
        <Box sx={{ mb: 10 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="subtitle1"
              sx={{
                color: colors.primary,
                fontWeight: 600,
                letterSpacing: 1,
                mb: 1
              }}
            >
              WHY CHOOSE DIAMONDBANK ZIMBABWE
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                color: colors.primary,
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -15,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '100px',
                  height: '4px',
                  background: `linear-gradient(90deg, ${colors.primary}, ${colors.secondary})`,
                  borderRadius: '2px'
                }
              }}
            >
              Loan Benefits
            </Typography>
            <Typography variant="h5" sx={{ mt: 3, color: colors.gray, fontWeight: 300 }}>
              Advantages that set our loan products apart in Zimbabwe
            </Typography>
          </Box>
          <Grid container spacing={4} justifyContent="center">
            {loanFeatures.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Grow in timeout={800} style={{ transitionDelay: `${index * 150}ms` }}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 4,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      borderRadius: '16px',
                      background: 'white',
                      boxShadow: `0 10px 30px ${alpha(colors.primary, 0.08)}`,
                      border: `1px solid ${alpha(colors.primary, 0.1)}`,
                      transition: 'all 0.3s ease',
                      width: '100%',
                      maxWidth: 300,
                      '&:hover': {
                        boxShadow: `0 15px 35px ${alpha(colors.primary, 0.15)}`,
                        borderColor: `${alpha(colors.primary, 0.2)}`,
                        transform: 'translateY(-5px)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        backgroundColor: `${alpha(colors.primary, 0.1)}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h5" gutterBottom sx={{ color: colors.primary, fontWeight: 600 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body1" sx={{ color: colors.gray }}>
                      {feature.description}
                    </Typography>
                  </Paper>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Application Process */}
        <Box sx={{ mb: 10 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="subtitle1"
              sx={{
                color: colors.primary,
                fontWeight: 600,
                letterSpacing: 1,
                mb: 1
              }}
            >
              GET STARTED IN ZIMBABWE
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                color: colors.primary,
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -15,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '100px',
                  height: '4px',
                  background: `linear-gradient(90deg, ${colors.primary}, ${colors.secondary})`,
                  borderRadius: '2px'
                }
              }}
            >
              Simple Application Process
            </Typography>
            <Typography variant="h5" sx={{ mt: 3, color: colors.gray, fontWeight: 300 }}>
              Get approved in just a few steps in Zimbabwe
            </Typography>
          </Box>
          <Paper
            elevation={3}
            sx={{
              p: { xs: 3, md: 6 },
              borderRadius: '16px',
              background: 'white',
              boxShadow: `0 10px 30px ${alpha(colors.primary, 0.08)}`,
              border: `1px solid ${alpha(colors.primary, 0.1)}`,
              maxWidth: 1200,
              mx: 'auto'
            }}
          >
            <Grid container spacing={4} justifyContent="center">
              {[
                {
                  step: "1",
                  title: "Submit Application",
                  description: "Complete our simple online form or visit a branch in Zimbabwe",
                  icon: <CheckCircle sx={{ color: colors.primary, fontSize: 40 }} />
                },
                {
                  step: "2",
                  title: "Document Review",
                  description: "Our team will verify your information within 24 hours",
                  icon: <Security sx={{ color: colors.primary, fontSize: 40 }} />
                },
                {
                  step: "3",
                  title: "Approval Decision",
                  description: "Receive a response within 48 hours of submission",
                  icon: <Schedule sx={{ color: colors.primary, fontSize: 40 }} />
                },
                {
                  step: "4",
                  title: "Funds Disbursement",
                  description: "Get your money quickly in ZWL after approval confirmation",
                  icon: <TrendingUp sx={{ color: colors.primary, fontSize: 40 }} />
                }
              ].map((step, index) => (
                <React.Fragment key={index}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Fade in timeout={800} style={{ transitionDelay: `${index * 200}ms` }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          textAlign: 'center',
                          height: '100%',
                          p: 3,
                          backgroundColor: `${alpha(colors.primary, 0.05)}`,
                          borderRadius: '12px',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-5px)',
                            boxShadow: `0 10px 20px ${alpha(colors.primary, 0.15)}`
                          }
                        }}
                      >
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            backgroundColor: `${alpha(colors.primary, 0.1)}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 3
                          }}
                        >
                          {step.icon}
                        </Box>
                        <Typography
                          variant="h4"
                          sx={{
                            color: colors.primary,
                            fontWeight: 700,
                            mb: 2
                          }}
                        >
                          Step {step.step}
                        </Typography>
                        <Typography variant="h6" gutterBottom sx={{ color: colors.primary, fontWeight: 600 }}>
                          {step.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.gray }}>
                          {step.description}
                        </Typography>
                      </Box>
                    </Fade>
                  </Grid>
                </React.Fragment>
              ))}
            </Grid>
            <Box sx={{ textAlign: 'center', mt: 6 }}>
              <Button
                variant="contained"
                size="large"
                href="/apply"
                sx={{
                  px: 6,
                  py: 2,
                  fontSize: '1.1rem',
                  borderRadius: '50px',
                  background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
                  boxShadow: `0 4px 20px ${alpha(colors.primary, 0.4)}`,
                  '&:hover': {
                    boxShadow: `0 6px 25px ${alpha(colors.primary, 0.5)}`,
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease',
                  fontWeight: 600
                }}
              >
                Start Your Application
              </Button>
            </Box>
          </Paper>
        </Box>

        {/* Calculator Tools Section */}
        <Box sx={{ mb: 10 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="subtitle1"
              sx={{
                color: colors.primary,
                fontWeight: 600,
                letterSpacing: 1,
                mb: 1
              }}
            >
              PLAN YOUR FINANCES IN ZWL
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                color: colors.primary,
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -15,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '100px',
                  height: '4px',
                  background: `linear-gradient(90deg, ${colors.primary}, ${colors.secondary})`,
                  borderRadius: '2px'
                }
              }}
            >
              Loan Calculators
            </Typography>
            <Typography variant="h5" sx={{ mt: 3, color: colors.gray, fontWeight: 300 }}>
              Use our free online tools to estimate your loan payments in Zimbabwean Dollars
            </Typography>
          </Box>
          <Grid container spacing={3} justifyContent="center">
            {calculatorTools.map((tool, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Grow in timeout={800} style={{ transitionDelay: `${index * 100}ms` }}>
                  <Paper
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      borderRadius: '12px',
                      background: 'white',
                      boxShadow: `0 4px 16px ${alpha(colors.primary, 0.08)}`,
                      border: `1px solid ${alpha(colors.primary, 0.1)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: `0 8px 24px ${alpha(colors.primary, 0.15)}`
                      }
                    }}
                  >
                    <Calculate sx={{ fontSize: 48, color: colors.primary, mb: 2 }} />
                    <Typography variant="h6" gutterBottom sx={{ color: colors.primary, fontWeight: 600 }}>
                      {tool.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.gray, mb: 2 }}>
                      {tool.description}
                    </Typography>
                    <Button
                      variant="outlined"
                      href={tool.link}
                      sx={{
                        borderColor: colors.primary,
                        color: colors.primary,
                        '&:hover': {
                          borderColor: colors.primary,
                          backgroundColor: alpha(colors.primary, 0.05)
                        }
                      }}
                    >
                      Calculate Now
                    </Button>
                  </Paper>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* CTA Section */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
            color: 'white',
            borderRadius: '16px',
            p: 8,
            mb: 6,
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: `radial-gradient(circle at 20% 50%, ${colors.secondary} 1px, transparent 1px)`,
              backgroundSize: '40px 40px',
              opacity: 0.1
            }
          }}
        >
          <Fade in timeout={800}>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography variant="h2" gutterBottom sx={{ fontWeight: 700 }}>
                Ready to Apply in Zimbabwe?
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, opacity: 0.9, fontWeight: 300 }}>
                Start your loan application today and get a decision within 48 hours
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  href="/apply"
                  sx={{
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                    borderRadius: '50px',
                    backgroundColor: colors.secondary,
                    color: colors.primary,
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: colors.white,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Apply Now
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  href="/contact"
                  sx={{
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                    borderRadius: '50px',
                    borderWidth: '2px',
                    borderColor: 'white',
                    color: 'white',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      borderWidth: '2px',
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  Speak to a Loan Officer
                </Button>
              </Box>
            </Box>
          </Fade>
        </Box>

        {/* Contact Section - Updated for Zimbabwe */}
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="h3" gutterBottom sx={{ color: colors.primary, fontWeight: 700 }}>
              Loan Resources - Zimbabwe
            </Typography>
            <Grid container spacing={2}>
              {[
                "Loan Calculators",
                "Interest Rates",
                "Payment Options",
                "Document Checklist",
                "FAQ",
                "Glossary",
                "Financial Tips",
                "Contact Support"
              ].map((link, index) => (
                <Grid item xs={6} sm={4} key={index}>
                  <Button
                    href={`/loans/${link.toLowerCase().replace(/\s+/g, '-')}`}
                    sx={{
                      justifyContent: 'flex-start',
                      color: colors.textDark,
                      textTransform: 'none',
                      fontWeight: 500,
                      '&:hover': {
                        color: colors.primary,
                        backgroundColor: 'transparent'
                      }
                    }}
                  >
                    {link}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                borderRadius: '16px',
                backgroundColor: colors.white,
                border: `1px solid ${alpha(colors.primary, 0.1)}`,
                height: '100%'
              }}
            >
              <Typography variant="h3" gutterBottom sx={{ color: colors.primary, fontWeight: 700 }}>
                Loan Support - Zimbabwe
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Phone sx={{ color: colors.primary, mr: 2 }} />
                <Typography variant="body1" sx={{ color: colors.textDark }}>
                  +263 242 708 888 (Loan Department - Zimbabwe)
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Email sx={{ color: colors.primary, mr: 2 }} />
                <Typography variant="body1" sx={{ color: colors.textDark }}>
                  loans@diamondbank.co.zw
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <LocationOn sx={{ color: colors.primary, mr: 2 }} />
                <Typography variant="body1" sx={{ color: colors.textDark }}>
                  80 Nelson Mandela Avenue, Harare, Zimbabwe
                </Typography>
              </Box>
              <Button
                variant="outlined"
                href="/locations"
                endIcon={<ArrowForward />}
                sx={{
                  borderRadius: '50px',
                  px: 4,
                  py: 1.5,
                  borderWidth: '2px',
                  borderColor: colors.primary,
                  color: colors.primary,
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: `${alpha(colors.primary, 0.05)}`,
                    borderWidth: '2px',
                    transform: 'translateX(5px)'
                  }
                }}
              >
                Find a Branch in Zimbabwe
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* CSS animations */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              transform: translateY(0);
            }
            50% {
              transform: translateY(-10px);
            }
          }

          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
    </Box>
  );
};

export default Loans;